import z from 'zod';
import { Image, Item, Price, Promo } from './Common';
import { VoucherSchema } from './Common.schema';
import { Delivery } from './Fulfillment';

export type BasketItemType =
  | 'vouchers'
  | 'events'
  | 'products'
  | 'spa-days'
  | 'spa-treatments'
  | 'rooms'
  | 'tables'
  | 'spa-packages'
  | 'golf';

export type EventOption = {
  event: null;
  eventOption: null;
  type: 'events';
  checkIn: null;
  checkOut: null;
  adults: null;
  children: null;
  bookable: null;
  guestDetails: Array<any>;
  expireAt: null;
  isDeferrable: false;
  deliveryDetails: null;
  personalization: null;
};

export type VoucherOption = {
  event: null;
  eventOption: null;
  type: 'vouchers';
  checkIn: null;
  checkOut: null;
  adults: null;
  children: null;
  bookable: null;
  guestDetails: Array<any>;
  expireAt: null;
  isDeferrable: false;
  deliveryDetails: null;
  personalization: null;
};

type Extras = {
  name: string;
  image: Image;
  price: Price;
  description: string;
};

export type GuestDetail = {
  id: number;
  title: string;
  gender: string;
  firstName: string;
  lastName: string;
  extras: Array<Extras>;
  externalSpaId: number | null;
  item: Array<number>;
};

export type Payment = {
  address: string;
  city: string;
  country: string;
  email: string;
  firstName: string;
  guestDetails?: GuestDetail;
  lastName: string;
  phoneNumber: string;
  phoneNumberPrefix: string;
  postalCode: string;
  region: string;
  title: string;
  loggedInUser?: { id: string };
};

type EmptyBasket = {
  id: string;
  items: Array<Item>;
  taxTotal: Price;
  total: Price;
  subtotal: Price;
};

export type BasketDetails = EmptyBasket & {
  sameAddressForBilling: boolean;
  activePromo: { name: string; discountValue: Promo } | null;
  delivery: Delivery | null;
  payment: Payment | null;
  currencyCode: string;
  updateGuestDetails: boolean;
  gatewayFee: Price | null;
  serviceFee: Price;
  registrationEmail: string | null;
  deferredPaymentNow: Price;
  deferredPaymentFuture: Price;
  deferredPaymentDate: Price | null;
  itemTotal: Price;
  discountTotals: {
    standardTotal: Price;
    discountedTotal: Price;
  } | null;
  domesticDeliveryCost: Price;
  internationalDeliveryCost: Price;
  deliveryCost?: Price;
  deferredPaymentEnabled: boolean;
  deferredPaymentAllowed?: boolean;
  fullPaymentAllowed?: boolean;
  notifications: null | BasketNotification[];
  promoCode?: string;
  vouchers: BasketVoucher[];
  depositOnly?: boolean;
  protectionEligibility?: ProtectionEligibility | null;
  protection?: BasketProtection | null;
};

export type ProtectionEligibility = {
  offered: boolean;
  eligibleValueAmount: number;
  eligibleItemIds: number[];
  earliestDeadline: string | null;
  earliestArrival: string | null;
  quoteFields?: Record<string, string>;
};

export type BasketProtection = {
  offered?: boolean;
  selected: boolean;
  quoteId: string | null;
  productId: number | null;
  productCode: string | null;
  premiumAmount: number | null;
  eligibleValueAmount: number | null;
};

export type BasketProtectionChoice = {
  selected: boolean;
  quoteId: string;
  productId?: number;
  productCode?: string;
};

export type BasketProtectionResponse = {
  protection: BasketProtection;
  eligibility: ProtectionEligibility;
};

type BasketNotification = {
  id: number;
  metadata: {
    type: string;
    name: string;
    bookableIds: (string | number)[];
    times: {
      checkIn: string | null;
      checkOut: string | null;
    };
    party: {
      adults: number | null;
      children: number | null;
    };
  };
};

export type EmptyGuest = {
  id: null;
};

export type Guest = {
  agreedToMarketingEmail: boolean;
  agreedToMarketingSms: boolean;
  externalSpaId: number | null;
  createdAt: string;
  delivery: Delivery | null;
  email: string;
  emailVerificationSentAt: string;
  emailVerified: boolean;
  firstName: string;
  id?: number;
  lastName: string;
  payment: Payment;
  sameAddressForBilling: boolean;
  title: string | null;
  gender: 'male' | 'female';
  loggedInUser?: { id: number };
  isLoggedIn?: boolean;
};

export type SelectionType = {
  option: {
    id: number;
  };
  startTime?: string;
  startDate?: string;
  endTime?: string;
  guestDetails?: Pick<Guest, 'id' | 'title' | 'firstName' | 'lastName' | 'gender'>;
  name?: string;
  price?: Price;
  extra?: { price: Price };
};

export type SelectionGroupType = {
  configurationGroup: {
    id: number;
  };
  isExtras?: boolean;
  selections: SelectionType[];
};

export type ExtraType = {
  extra: {
    id: number;
  };
  guestDetails: Pick<Guest, 'id' | 'title' | 'firstName' | 'lastName' | 'gender'>;
};

export type ExtraSelectionGroupType = {
  isExtras: boolean;
  selections: ExtraType[];
};

export type SelectionOrExtraType = SelectionGroupType | ExtraSelectionGroupType;

export type SpaPackageBasketItem = {
  type: 'spa-packages';
  spaPackage: {
    id: number;
  };
  checkIn: string;
  checkOut: string;
  guestDetails: Pick<Guest, 'id' | 'title' | 'firstName' | 'lastName' | 'gender'>[];
  selectionGroups?: SelectionOrExtraType[];
  note?: string;
};

export type BasketVoucher = z.input<typeof VoucherSchema>;

type PricedBasketItem = {
  type?: string;
  price?: { amount?: number | null } | null;
  bookable?: { price?: { amount?: number | null } | null } | null;
  discount?: { standardPrice?: { amount?: number | null } | null } | null;
};

const originalAmount = (item: PricedBasketItem) =>
  item.bookable?.price?.amount ?? item.discount?.standardPrice?.amount ?? item.price?.amount ?? 0;

export const isNoValueTableBooking = (item: PricedBasketItem | null | undefined): boolean =>
  item?.type === 'tables' && originalAmount(item) === 0;

export const hasOnlyNoValueTableBookings = (items: unknown): boolean => {
  const list = Array.isArray(items) ? (items as PricedBasketItem[]) : [];
  return list.length > 0 && list.every(isNoValueTableBooking);
};

type TablePreferenceItem = {
  dietaryRequirements?: unknown;
  celebrations?: unknown;
  amendments?: unknown;
};

export type TablePreferences = {
  dietaryRequirements: string[];
  celebrations: string[];
};

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : [];

const amendmentList = (amendments: unknown[], type: string): string[] =>
  stringList(
    (
      amendments.find((amendment) => (amendment as { type?: unknown } | null)?.type === type) as
        | { amendments?: unknown }
        | undefined
    )?.amendments
  );

export const tablePreferences = (
  item: TablePreferenceItem | null | undefined
): TablePreferences => {
  const amendments = Array.isArray(item?.amendments) ? item.amendments : [];
  if (amendments.length > 0) {
    return {
      dietaryRequirements: amendmentList(amendments, 'dietary_requirements'),
      celebrations: amendmentList(amendments, 'celebrations'),
    };
  }
  return {
    dietaryRequirements: stringList(item?.dietaryRequirements),
    celebrations: stringList(item?.celebrations),
  };
};

export const tablePreferenceLines = (
  item: TablePreferenceItem | null | undefined,
  labels: { dietaryRequirements: string; celebrations: string }
): string[] => {
  const { dietaryRequirements, celebrations } = tablePreferences(item);
  return [
    ...(dietaryRequirements.length > 0
      ? [`${labels.dietaryRequirements}: ${dietaryRequirements.join(', ')}`]
      : []),
    ...(celebrations.length > 0 ? [`${labels.celebrations}: ${celebrations.join(', ')}`] : []),
  ];
};
